import { type } from 'arktype';
import type { CostRateScope } from './cost-rate.model';
import { isoDateSchema } from './project.model';

/**
 * Invoice models (spec: faktura.md, Story 4-7): the Faktura editor
 * (`/projects/admin/invoices/new`), the invoiceable-entries position
 * suggestion, the persisted invoice + lines, and the PDF/QR generation
 * status surfaced by the invoice list (`/projects/admin/:id/invoices`).
 */

/** Mirrors `documents.generated_documents.status` — `null` when no PDF job has been submitted (or submission itself failed synchronously). */
export const INVOICE_PDF_STATUSES = ['pending', 'processing', 'completed', 'failed'] as const;
export type InvoicePdfStatus = (typeof INVOICE_PDF_STATUSES)[number] | null;

/** Fixed Swiss VAT rate set (mirrors `billing_settings`/`invoice_lines` CHECK constraints). */
export const VAT_RATES = [8.1, 2.6, 3.8, 0] as const;
export type VatRate = (typeof VAT_RATES)[number];

export const vatRateSchema = type('8.1|2.6|3.8|0').configure({
  description: 'Swiss VAT rate in percent.',
  examples: [8.1],
});

/**
 * One activity-type group of invoiceable bookings — the position-suggestion
 * source of the Faktura editor. `entryIds` is sent back unmodified when the
 * resulting invoice line should mark these bookings as invoiced.
 */
export interface InvoiceableEntryGroup {
  activityTypeId: string;
  activityTypeName: string;
  /** Sum of booked hours (minutes / 60), two decimals. */
  quantityHours: number;
  /** Effective external rate resolved via the cost-rate hierarchy (spec: faktura.md, Story 2). */
  rate: number;
  rateSource: CostRateScope;
  entryIds: string[];
}

/** Frozen sender master data as stored on the invoice (never changes retroactively). */
export interface InvoiceSenderSnapshot {
  companyName: string;
  addressLine: string;
  postalCity: string;
  vatNumber: string | null;
  iban: string;
}

/** Current sender master data plus the defaults the Faktura editor prefills the header with. */
export interface InvoiceSender extends InvoiceSenderSnapshot {
  defaultPaymentTermDays: number;
  defaultVatRate: number;
  footerNote: string | null;
}

/**
 * Update payload for `PUT /project-billing-settings` (Settings → Rechnungseinstellungen).
 * `postalCity` must already be `"<4-digit zip> <city>"` — the same shape
 * `renderQrBillSvg`/`parseSwissPostalCity` require to build the QR-bill —
 * so a malformed value fails fast here with a 400 instead of only surfacing
 * later as a 409 when someone tries to preview/create an invoice.
 */
export const updateBillingSettingsSchema = type({
  companyName: type('string>=1 & string<=200').configure({ examples: ['Acme AG'] }),
  addressLine: type('string>=1 & string<=200').configure({ examples: ['Musterstrasse 1'] }),
  postalCity: type('string>=1 & string<=200')
    .narrow(
      (value, ctx) =>
        /^\d{4}[ \t]+[^\r\n]{1,100}$/.test(value.trim()) || ctx.mustBe('formatted as "<4-digit zip> <city>", e.g. "8000 Zürich"')
    )
    .configure({ examples: ['8000 Zürich'] }),
  'vatNumber?': type('string<=50').or('null'),
  iban: type('string>=1 & string<=34').configure({ examples: ['CH9300762011623852957'] }),
  defaultPaymentTermDays: type('1 <= number <= 365').narrow(
    (value, ctx) => Number.isInteger(value) || ctx.mustBe('a whole number of days')
  ),
  defaultVatRate: vatRateSchema,
  'footerNote?': type('string<=1000').or('null'),
}).configure({ description: 'Updates the invoice sender master data (Rechnungseinstellungen).' });
export type UpdateBillingSettings = typeof updateBillingSettingsSchema.t;

export interface InvoiceLine {
  id: string;
  description: string;
  quantityHours: number;
  rate: number;
  vatRate: number;
  amount: number;
  activityTypeId: string | null;
}

export interface Invoice {
  id: string;
  number: string;
  projectId: string;
  issueDate: string;
  dueDate: string;
  subject: string;
  periodLabel: string | null;
  sender: InvoiceSenderSnapshot;
  recipientName: string;
  recipientAddress: string | null;
  recipientCity: string | null;
  currency: 'CHF';
  netAmount: number;
  vatAmount: number;
  roundingAmount: number;
  grossAmount: number;
  paymentTermsNote: string | null;
  /** ISO 11649 (SCOR) creditor reference used on the QR-bill. `null` until PDF generation has been submitted at least once. */
  qrReference: string | null;
  /** Id of the `generated_documents` row backing the current PDF attempt. `null` until submission succeeds. */
  documentId: string | null;
  /** Status of the `documentId` PDF job — `null` when no (successfully submitted) job exists yet. */
  pdfStatus: InvoicePdfStatus;
  createdAt: string;
  lines: InvoiceLine[];
}

/** Summary row of the invoice list (spec: faktura.md, Story 7) — omits `lines`/`sender` (not needed by the list). */
export interface InvoiceListItem {
  id: string;
  number: string;
  projectId: string;
  issueDate: string;
  dueDate: string;
  subject: string;
  grossAmount: number;
  documentId: string | null;
  pdfStatus: InvoicePdfStatus;
  createdAt: string;
}

/**
 * Bounds mirror the `numeric(8, 2)` / `numeric(10, 2)` columns: at most two
 * decimal places — anything else would surface as a Postgres check-constraint
 * violation (500) instead of a friendly 400.
 */
const quantityHoursSchema = type('0 <= number <= 99999.99').narrow(
  (value, ctx) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 || ctx.mustBe('a number with at most two decimal places')
);
const invoiceRateSchema = type('0 <= number <= 999999.99').narrow(
  (value, ctx) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 || ctx.mustBe('a number with at most two decimal places')
);
const descriptionSchema = type('string>=1 & string<=500').configure({ description: 'Line description shown on the invoice.' });
const entryIdsSchema = type('string.uuid')
  .array()
  .atMostLength(500)
  .configure({ description: 'Ids of the time entries this line invoices. Omit/empty for a manual line without a booking reference.' });

export const createInvoiceLineSchema = type({
  description: descriptionSchema,
  quantityHours: quantityHoursSchema.configure({ description: 'Quantity in hours.', examples: [12.5] }),
  rate: invoiceRateSchema.configure({ description: 'External rate (CHF/hour).', examples: [120] }),
  vatRate: vatRateSchema,
  'activityTypeId?': type('string.uuid')
    .or('null')
    .configure({ description: 'Activity type this line originates from. `null`/omitted for a manual line.' }),
  'entryIds?': entryIdsSchema,
}).configure({ description: 'One invoice line, either derived from bookings (activityTypeId + entryIds set) or entered manually.' });
export type CreateInvoiceLine = typeof createInvoiceLineSchema.t;

const paymentTermDaysSchema = type('1 <= number <= 365').narrow(
  (value, ctx) => Number.isInteger(value) || ctx.mustBe('a whole number of days')
);

export const createInvoiceSchema = type({
  projectId: type('string.uuid').configure({ description: 'Project this invoice bills.' }),
  'issueDate?': isoDateSchema.configure({ description: 'Invoice date. Defaults to today.' }),
  'paymentTermDays?': paymentTermDaysSchema.configure({
    description: 'Days until due. Defaults to `billing_settings.default_payment_term_days`.',
  }),
  subject: type('string>=1 & string<=200').configure({ description: 'Invoice subject / title.', examples: ['Projektstunden Juni 2026'] }),
  'periodLabel?': type('string<=200').or('null').configure({ description: 'Free-text billed period, e.g. "Juni 2026".' }),
  recipientName: type('string>=1 & string<=200').configure({ description: 'Invoice recipient (company/person name).' }),
  'recipientAddress?': type('string<=200').or('null'),
  'recipientCity?': type('string<=200').or('null'),
  lines: createInvoiceLineSchema.array().atLeastLength(1).atMostLength(200).configure({ description: 'Invoice lines, at least one.' }),
  'templateVersionId?': type('string.uuid').configure({
    description: 'Invoice template version to render with. Defaults to the active version.',
  }),
}).configure({ description: 'Creates an invoice: persists header + lines transactionally and marks the referenced bookings as invoiced.' });
export type CreateInvoice = typeof createInvoiceSchema.t;
